// @ts-check
/** @typedef {import('../types/api').SeoHealth} SeoHealth */
/** @typedef {import('../types/api').SeoProduct} SeoProduct */
/** @typedef {import('../types/api').PaginatedSeoProducts} PaginatedSeoProducts */
/** @typedef {import('../types/api').SeoCollection} SeoCollection */

import api from '../utils/apiClient';
import { assertArray, assertShape } from '../utils/apiGuards';

const adminSeoService = {
  /**
   * @returns {Promise<SeoHealth>}
   */
  async getHealth() {
    const data = await api.get('/api/admin/seo/health');
    // Backend returns {overall_score, products, collections}, not a
    // top-level 'total_products' key — asserting that threw on every
    // valid response, always surfacing as "Failed to load SEO health data".
    assertShape(data, ['overall_score', 'products', 'collections'], 'adminSeoService.getHealth');
    return data;
  },

  /**
   * @param {Object} [params]
   * @returns {Promise<PaginatedSeoProducts>}
   */
  async getProducts(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.filter) queryParams.append('filter', params.filter);
    if (params.search) queryParams.append('search', params.search);
    const data = await api.get(`/api/admin/seo/products?${queryParams}`);
    assertShape(data, ['items', 'total'], 'adminSeoService.getProducts');
    return data;
  },

  /**
   * @param {string} productId
   * @param {Object} data
   * @returns {Promise<SeoProduct>}
   */
  async updateProductSeo(productId, data) {
    return api.patch(`/api/admin/seo/products/${productId}/seo`, data);
  },

  /**
   * @returns {Promise<SeoCollection[]>}
   */
  async getCollections() {
    const data = await api.get('/api/admin/seo/collections');
    assertArray(data, 'adminSeoService.getCollections');
    return data;
  },

  /**
   * @param {string} collectionId
   * @param {Object} data
   * @returns {Promise<SeoCollection>}
   */
  async updateCollectionSeo(collectionId, data) {
    return api.patch(`/api/admin/seo/collections/${collectionId}/seo`, data);
  },

  /**
   * @param {{name: string, description?: string, short_description?: string, kind?: 'item' | 'page', path?: string, brand_context?: string}} facts
   * @returns {Promise<{seo_title: string, seo_description: string}>}
   */
  async generate(facts) {
    return api.post('/api/admin/seo/generate', facts);
  },
};

export default adminSeoService;
